//-------------------------------------------------------------------------------------------------- Imports
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getGeneralData } from "../../stores/slices/generalDataSlice";
import SubDefaultLayout from "../../components/SubDefaultLayout";
import InfoCardComponent from "../../components/InfoCardComponent";
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { Card, Col, Row } from "antd";
import PaymentAmount from "./components/PaymentAmount";
import { GenericResponse, ResponsePaymentGeneral, ResponseSuscriptorDataGeneralDatum, Tarjeta } from "../../types/common";
import { getExpirationCard, getPaymentsGeneral, getRecentPayment, paypalCancel, paypalGenerate, paypalId, paypalStatus } from "../../methods/Methods";
import { AppDispatch } from "../../stores/store";
import { AlertSliceInterface, clearAlert, setAlert, showAlert } from "../../stores/slices/alertSlice";
import { clearPaymentData, getPaymentDataContracts, setPaymentDataToContract } from "../../stores/slices/paymentDataSlice";
import _ from "lodash";
import Loading from "../../components/Loading";

//-------------------------------------------------------------------------------------------------- View
const PaypalPayment = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [hasError, setHasError] = useState(false);
    const [message, setMessage] = useState("");
    const generalData = useSelector(getGeneralData);
    const [dataClient, setDataClient] = useState<ResponseSuscriptorDataGeneralDatum>();
    const paymentDataRedux = useSelector(getPaymentDataContracts);
    const [idCliente, setIdCliente] = useState<string>("");
    const [paypalClient, setPaypalClient] = useState<string>("");
    const [paymentData, setPaymentData] = useState<ResponsePaymentGeneral>();
    const CryptoJS = require('crypto-js');
    const [hasPayment, setHasPayment] = useState<boolean>(false);
    const [messageHasPayment, setMessageHasPayment] = useState("");

    const openAlertModal = useCallback((option: AlertSliceInterface) => {
        dispatch(setAlert({
            show: option.show,
            type: option.type as "INFO" | "SUCCESS" | "ERROR",
            title: option.title,
            message: option.message,
            onFinalize: option.onFinalize,
            isReceiptPayment: option.isReceiptPayment,
            paymentData: option.paymentData
        }));
    }, [dispatch]);

    const getPaymentData = useCallback(async () => {
        setIsLoading(true);
        await getPaymentsGeneral(idCliente)
            .then(async (response: GenericResponse<ResponsePaymentGeneral>) => {
                if (!_.isEmpty(response.data)) {
                    let recurrentCard: Tarjeta = {
                        terminacion: "",
                        vencimiento: {
                            mes: 0,
                            anio: 0
                        }
                    };
                    await getExpirationCard(idCliente)
                        .then((response) => {
                            if (response.data && response.data.data[0]) {
                                //Si el campo tarjeta no está null es porque tiene tarjeta domiciliada
                                //Y si es diferente del cod 902
                                if (!_.isNull(response.data.data[0].tarjeta) && response.data.data[0].codigo !== "902") {
                                    recurrentCard = response.data.data[0].tarjeta;
                                }
                            }
                        })
                        .catch(() => {
                        });
                    setPaymentData(response.data[0]);
                    dispatch(setPaymentDataToContract({
                        clientId: idCliente,
                        paymentData: response.data[0],
                        recurrentCard: recurrentCard
                    }));
                }
                else {
                    setPaymentData(undefined);
                }
                setIsLoading(false);
            }).catch(() => {
                setPaymentData(undefined);
                setIsLoading(false);
            });
    }, [dispatch, idCliente]);

    const getPaypalId = useCallback(async () => {
        if (idCliente) {
            await paypalId(idCliente)
                .then((response) => {
                    if (response.data.data[0].respuesta === "OK") {
                        setPaypalClient(response.data.data[0].mensaje)
                    }
                })
                .catch(() => {
                    setHasError(true);
                    setMessage("Hubo un error al procesar su solicitud. \nIntente más tarde.");
                });
        }
    }, [idCliente]);

    const initialOptions = {
        "client-id": paypalClient,
        currency: "MXN",
        components: "buttons",
    };

    function _arrayBufferToBase64(buffer: any) {
        var binary = '';
        var bytes = new Uint8Array(buffer);
        var len = bytes.byteLength;
        for (var i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    function base64toHEX(base64: any) {
        var raw = atob(base64);
        var HEX = '';
        for (let i = 0; i < raw.length; i++) {
            var _hex = raw.charCodeAt(i).toString(16)
            HEX += (_hex.length === 2 ? _hex : '0' + _hex);
        }
        return HEX.toUpperCase();
    }

    function cifrarAES(data: any, password: any) {
        let buf = new Uint8Array(16);
        window.crypto.getRandomValues(buf); //Obtenermos 16 bytes
        let buffer_b64 = _arrayBufferToBase64(buf); //
        let iv = CryptoJS.enc.Hex.parse(base64toHEX(buffer_b64));
        var key = CryptoJS.enc.Hex.parse(password);

        let cipherParams = {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7
        }

        let encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), key, cipherParams);

        let merge = encrypted.iv.clone();
        merge.concat(encrypted.ciphertext);
        let merge2 = CryptoJS.enc.Base64.stringify(merge);

        return merge2;
    };

    const createOrder = async () => {
        let sucursal = idCliente.substring(0, 3);
        let suscriptor = idCliente.substring(3, 10);

        let objData = {
            sucursal: sucursal,
            suscriptor: suscriptor,
            monto: paymentData?.payment.datos?.amount.replace(/,/g, ""),
            origen: "WEN",
            returnUrl: "https://example.com/return",
            cancelarUrl: "https://example.com/return",
            metadata: "",
        };

        let dataEncrypted = cifrarAES(objData, "5DCC67393750523CD165F17E1EFADD21");

        let objDataEncrypted = {
            param: dataEncrypted,
        };

        let orderTmp = "";
        await paypalGenerate(objDataEncrypted, idCliente)
            .then((response) => {
                if (response.data.data[0].estatus === "1") {
                    orderTmp = response.data.data[0].id_operacion;
                } else if (response.data.data[0].estatus === "4") {
                    let dispatchAlert: AlertSliceInterface = {
                        show: true,
                        type: "ERROR",
                        title: "Pago PayPal",
                        message: response.data.data[0].mensaje,
                        onFinalize: () => handleDispatchAlertModal()
                    }
                    openAlertModal(dispatchAlert);
                } else {
                    let dispatchAlert: AlertSliceInterface = {
                        show: true,
                        type: "ERROR",
                        title: "Pago PayPal",
                        message: "Su pago no ha sido procesado con PayPal, intente más tarde.",
                        onFinalize: () => handleDispatchAlertModal()
                    }
                    openAlertModal(dispatchAlert);
                }
            })
            .catch((error) => {
                let dispatchAlert: AlertSliceInterface = {
                    show: true,
                    type: "ERROR",
                    title: "Pago PayPal",
                    message: "Su pago no ha sido procesado con PayPal, intente más tarde.",
                    onFinalize: () => handleDispatchAlertModal()
                }
                openAlertModal(dispatchAlert);
            });

        return orderTmp;
    };

    const onApprove = async (data: any) => {
        let sucursal = idCliente.substring(0, 3);
        let suscriptor = idCliente.substring(3, 10);
        let objData = {
            sucursal: sucursal,
            suscriptor: suscriptor,
            id_operacion: data.orderID,
            origen: "NPP",
            metadata: "",
        };
        await paypalStatus(objData, idCliente)
            .then((response) => {
                if (response.data.data[0].estatus === "1") {
                    const fecha = new Date(response.data.data[0].fecha);
                    const dateFormatted = fecha.toLocaleDateString('es-MX', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    });
                    const hourFormatted = fecha.toLocaleTimeString('es-MX', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    });

                    let dispatchStatusPay: AlertSliceInterface = {
                        show: true,
                        type: "SUCCESS",
                        title: "Tu pago PayPal ha sido exitoso",
                        isReceiptPayment: true,
                        message: "",
                        paymentData: {
                            amount: response.data.data[0].monto ? response.data.data[0].monto : paymentData?.payment.datos?.amount,
                            idTransaction: response.data.data[0].id_transaccion ? response.data.data[0].id_transaccion : undefined,
                            client: idCliente,
                            name: dataClient?.nombre + " " + dataClient?.apellidoPaterno + " " + dataClient?.apellidoMaterno,
                            date: dateFormatted,
                            time: hourFormatted,
                            concept: dataClient?.paquete
                        },
                        onFinalize: () => handleDispatchAlertModal()
                    }
                    openAlertModal(dispatchStatusPay);
                    dispatch(clearPaymentData());
                } else {
                    let dispatchAlert: AlertSliceInterface = {
                        show: true,
                        type: "ERROR",
                        title: "Pago PayPal",
                        message: "Su pago no ha sido procesado con PayPal, intente más tarde.",
                        onFinalize: () => handleDispatchAlertModal()
                    }
                    openAlertModal(dispatchAlert);
                }
            })
            .catch(() => {
                let dispatchAlert: AlertSliceInterface = {
                    show: true,
                    type: "ERROR",
                    title: "Pago PayPal",
                    message: "No fue posible pagar con PayPal, intenta más tarde.",
                    onFinalize: () => handleDispatchAlertModal()
                }
                openAlertModal(dispatchAlert);
            });
    };

    const onCancel = async (data: any) => {
        let objData = {
            id_operacion: data.orderID,
            cancelar_url: "https://example.com/return",
        };

        await paypalCancel(objData, idCliente)
            .then((response) => {
                let dispatchAlert: AlertSliceInterface = {
                    show: true,
                    type: "INFO",
                    title: "Cancelación PayPal",
                    message: response.data.data[0].mensaje,
                    onFinalize: () => handleDispatchAlertModal()
                }
                openAlertModal(dispatchAlert);
            })
            .catch(() => { });
    };

    const handleDispatchAlertModal = useCallback(() => {
        dispatch(showAlert(false));
        dispatch(clearAlert());
    }, [dispatch]);

    useEffect(() => {
        if (generalData.selectedContract !== null) {
            setIdCliente(
                generalData.selectedContract.sucursal + generalData.selectedContract.suscriptor
            );
            setDataClient(generalData.selectedContract);
        }
    }, [generalData.selectedContract]);

    useEffect(() => {
        if (idCliente) {
            setIsLoading(true);
            getRecentPayment(idCliente)
                .then((response) => {
                    if (response.data.data[0].status) {
                        if (response.data.data[0].hasPayment) {
                            setMessageHasPayment(response.data.data[0].message);
                            setHasPayment(true);
                        }
                        setIsLoading(false);
                    } else {
                        setMessageHasPayment("Por el momento no es posible mostrar información de su contrato, intente más tarde.");
                        setHasPayment(true);
                        setIsLoading(false);
                    }
                })
                .catch(() => {
                    setMessageHasPayment("Por el momento no es posible mostrar información de su contrato, intente más tarde.");
                    setHasPayment(true);
                    setIsLoading(false);
                })
        }
    }, [idCliente]);

    useEffect(() => {
        if (idCliente) {
            const index = paymentDataRedux.contracts.findIndex(obj => obj.clientId === idCliente);
            if (index >= 0) {
                setPaymentData(paymentDataRedux.contracts[index].paymentData);
            }
            else {
                getPaymentData();
            }
        }
    }, [idCliente, getPaymentData, paymentDataRedux.contracts]);

    useEffect(() => {
        if (idCliente && !_.isEmpty(idCliente)) {
            getPaypalId();
        }
    }, [idCliente, getPaypalId]);

    return (
        <>
            {
                isLoading ?
                    <Loading />
                    :
                    <SubDefaultLayout
                        title={"Pagar con Paypal"}
                        needReturn={true}
                        navigateTo={"/payments"}
                    >
                        <div style={{ marginTop: 20 }}>
                            {
                                hasError ?
                                    <InfoCardComponent title="¡Estimado usuario!" description={message} />
                                    :
                                    <>
                                        <InfoCardComponent
                                            title="¡Estimado Usuario!"
                                            description={"Por el momento no aceptamos pagos con American Express."}
                                        />
                                        <Row gutter={[12, 12]} style={{ marginTop: 20 }}>
                                            <Col md={12}>
                                                <PaymentAmount
                                                    paymentData={paymentData}
                                                    packageName={generalData.selectedContract?.paquete ? generalData.selectedContract?.paquete : ""}
                                                />
                                            </Col>
                                            <Col xs={24} md={13} lg={12}>
                                                {
                                                    paymentData?.payment.datos && !_.isEmpty(paymentData?.payment.datos.amount) && paymentData?.payment.datos.amount !== "0.00" ?
                                                        <>
                                                            {
                                                                hasPayment ?
                                                                    <InfoCardComponent color="#FFC439" colorText="#000" description={messageHasPayment} />
                                                                    :
                                                                    <Card style={{ backgroundColor: "transparent", padding: 0, }}>
                                                                        {
                                                                            paypalClient ?
                                                                                <PayPalScriptProvider options={initialOptions}>
                                                                                    <PayPalButtons
                                                                                        style={{
                                                                                            color: "gold",
                                                                                            shape: "rect",
                                                                                            label: "paypal",
                                                                                            tagline: false,
                                                                                            layout: "horizontal",
                                                                                        }}
                                                                                        createOrder={createOrder}
                                                                                        onApprove={onApprove}
                                                                                        onCancel={onCancel}
                                                                                    />
                                                                                </PayPalScriptProvider>
                                                                                :
                                                                                null
                                                                        }
                                                                    </Card>
                                                            }
                                                        </>
                                                        :
                                                        <InfoCardComponent title="¡No tienes adeudos!" />
                                                }

                                            </Col>
                                        </Row>
                                    </>
                            }
                        </div>
                    </SubDefaultLayout>
            }
        </>
    );
};

//-------------------------------------------------------------------------------------------------- Exports
export default PaypalPayment;
